import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import {
  buildPoseFileJson,
  splitSampleTimes,
  SPLIT_FPS,
  blazePoseLandmarkName,
  FrameLandmarks,
} from "../pose/clientPose";
import { extractionReducer, initialExtractionState } from "../pose/extractionState";
import { MODEL_VERSION, MODEL_URL, WASM_ROOT } from "../pose/mediapipeConfig";

/**
 * web-pose-extraction step 4: the on-device extraction screen. Runs MediaPipe PoseLandmarker over
 * the picked video at the split cadence, then lets the user play/scrub the frames, see the
 * skeleton with per-joint values, drag a joint to tweak it, and reset a frame to the detected
 * pose. Emits the pose file JSON (or null while incomplete) via onPoseFile.
 */

// The joints worth showing values for (the ones the backend consumes).
const VALUE_JOINTS = [0, 11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28];
const SKELETON_EDGES: Array<[number, number]> = [
  [11, 12], [11, 13], [13, 15], [12, 14], [14, 16],
  [11, 23], [12, 24], [23, 24], [23, 25], [25, 27], [24, 26], [26, 28],
];

type Props = {
  previewUrl: string;
  durationSec: number;
  onPoseFile: (json: string | null) => void;
  /** The cadence the server will split this upload at (frames pair with the split BY INDEX, so
   * the samples must sit exactly where the split frames will). The fixed 10 unless given. */
  fps?: number;
};

export default function ExtractionStep({ previewUrl, durationSec, onPoseFile, fps = SPLIT_FPS }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [state, dispatch] = useReducer(extractionReducer, undefined, initialExtractionState);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const dims = useRef({ w: 0, h: 0 });
  const dragging = useRef<number | null>(null);

  const sampleTimes = splitSampleTimes(durationSec, fps);

  // ---- extraction run ---------------------------------------------------------------------------
  const runExtraction = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    onPoseFile(null);
    setError(null);
    dispatch({ type: "START", totalFrames: sampleTimes.length });
    try {
      const { FilesetResolver, PoseLandmarker } = await import("@mediapipe/tasks-vision");
      const fileset = await FilesetResolver.forVisionTasks(WASM_ROOT);
      const landmarker = await PoseLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_URL },
        runningMode: "VIDEO",
        numPoses: 1,
      });
      dims.current = { w: video.videoWidth, h: video.videoHeight };
      for (const t of sampleTimes) {
        await seekTo(video, t);
        const res = landmarker.detectForVideo(video, Math.round(t * 1000));
        const lm = res.landmarks?.[0] ?? [];
        const frame: FrameLandmarks = {
          landmarks: lm
            .map((p: any, index: number) => ({ index, x: p.x, y: p.y, z: p.z, visibility: p.visibility }))
            .filter((p: any) => blazePoseLandmarkName(p.index) !== undefined),
        };
        dispatch({ type: "FRAME_DONE", frame });
      }
      landmarker.close();
      dispatch({ type: "COMPLETE" });
    } catch (e: any) {
      setError(e?.message ?? "Pose extraction failed — the upload can continue without it.");
      onPoseFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl, durationSec, fps]);

  useEffect(() => {
    // fresh video = fresh extraction
    dispatch({ type: "START", totalFrames: 0 });
    const video = videoRef.current;
    if (!video) return;
    const start = () => { runExtraction(); };
    if (video.readyState >= 2) start();
    else video.addEventListener("loadeddata", start, { once: true });
    return () => video.removeEventListener("loadeddata", start);
  }, [runExtraction]);

  // Emit the pose file whenever review-state frames change (incl. after tweaks).
  useEffect(() => {
    if (state.phase !== "review" || state.frames.length === 0) return;
    onPoseFile(
      buildPoseFileJson(state.frames, {
        captureWidth: dims.current.w,
        captureHeight: dims.current.h,
        modelVersion: MODEL_VERSION,
        fps,
      })
    );
  }, [state.phase, state.frames, onPoseFile, fps]);

  // ---- review: seek + draw ----------------------------------------------------------------------
  useEffect(() => {
    if (state.phase !== "review") return;
    const video = videoRef.current;
    if (!video) return;
    seekTo(video, sampleTimes[state.currentFrame] ?? 0).then(draw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentFrame, state.phase, state.frames]);

  // play mode: advance frames on a timer at ~the split cadence
  useEffect(() => {
    if (!playing || state.phase !== "review") return;
    const id = setInterval(() => {
      dispatch({ type: "GOTO_FRAME", index: (state.currentFrame + 1) % state.frames.length });
    }, Math.round(1000 / fps));
    return () => clearInterval(id);
  }, [playing, state.phase, state.currentFrame, state.frames.length, fps]);

  const draw = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || state.phase !== "review") return;
    const frame = state.frames[state.currentFrame];
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const g = canvas.getContext("2d")!;
    g.drawImage(video, 0, 0);
    if (!frame) return;
    const P = (i: number) => frame.landmarks.find((l) => l.index === i);
    g.lineWidth = Math.max(2, canvas.width / 300);
    g.strokeStyle = "#00ea3c";
    for (const [a, b] of SKELETON_EDGES) {
      const pa = P(a); const pb = P(b);
      if (!pa || !pb) continue;
      g.beginPath();
      g.moveTo(pa.x * canvas.width, pa.y * canvas.height);
      g.lineTo(pb.x * canvas.width, pb.y * canvas.height);
      g.stroke();
    }
    for (const l of frame.landmarks) {
      if (!VALUE_JOINTS.includes(l.index)) continue;
      g.beginPath();
      g.fillStyle = state.tweakedFrames.has(state.currentFrame) ? "#FFA500" : "#00CCFF";
      g.arc(l.x * canvas.width, l.y * canvas.height, Math.max(4, canvas.width / 160), 0, Math.PI * 2);
      g.fill();
    }
  }, [state]);

  useEffect(draw, [draw]);

  // ---- tweak: drag the nearest joint -------------------------------------------------------------
  const canvasPoint = (e: React.PointerEvent) => {
    const canvas = canvasRef.current!;
    const r = canvas.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (state.phase !== "review") return;
    const p = canvasPoint(e);
    const frame = state.frames[state.currentFrame];
    let best = -1, bestD = 0.04; // only grab within ~4% of the frame
    for (const l of frame?.landmarks ?? []) {
      if (!VALUE_JOINTS.includes(l.index)) continue;
      const d = Math.hypot(l.x - p.x, l.y - p.y);
      if (d < bestD) { bestD = d; best = l.index; }
    }
    if (best >= 0) {
      dragging.current = best;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (dragging.current == null) return;
    const p = canvasPoint(e);
    dispatch({ type: "TWEAK", frameIndex: state.currentFrame, landmarkIndex: dragging.current, x: p.x, y: p.y });
  };

  const onPointerUp = () => { dragging.current = null; };

  // ---- render ------------------------------------------------------------------------------------
  const frame = state.frames[state.currentFrame];
  return (
    <div className="rounded-2xl border border-slate-200 p-4 space-y-3 bg-white">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">On-device pose extraction</h2>
        {state.phase === "review" && state.tweakedFrames.has(state.currentFrame) && (
          <button
            type="button"
            onClick={() => dispatch({ type: "RESET_FRAME", frameIndex: state.currentFrame })}
            className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700"
          >
            <RotateCcw className="h-3 w-3" /> reset frame to detected
          </button>
        )}
      </div>

      {/* hidden video drives seeking; canvas is the display */}
      <video ref={videoRef} src={previewUrl} className="hidden" muted playsInline preload="auto" />
      <canvas
        ref={canvasRef}
        className="w-full rounded-lg bg-black touch-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      />

      {state.phase === "extracting" && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="h-3 w-3 animate-spin" />
            Extracting pose {state.frames.length}/{state.totalFrames} frames…
          </div>
          <div className="h-2 rounded bg-slate-100 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${state.progress * 100}%` }} />
          </div>
        </div>
      )}

      {state.phase === "review" && (
        <>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              className="px-3 py-1 text-xs rounded-full bg-slate-900 text-white"
            >
              {playing ? "pause" : "play"}
            </button>
            <input
              type="range"
              min={0}
              max={Math.max(0, state.frames.length - 1)}
              value={state.currentFrame}
              onChange={(e) => { setPlaying(false); dispatch({ type: "GOTO_FRAME", index: Number(e.target.value) }); }}
              className="flex-1 accent-emerald-500"
            />
            <span className="text-xs text-slate-500 tabular-nums">
              {state.currentFrame + 1}/{state.frames.length}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-[11px] text-slate-600 font-mono">
            {(frame?.landmarks ?? [])
              .filter((l) => VALUE_JOINTS.includes(l.index))
              .map((l) => (
                <div key={l.index} className="flex justify-between gap-2">
                  <span className="truncate">{blazePoseLandmarkName(l.index)}</span>
                  <span className="tabular-nums text-slate-400">
                    {(l.x * 100).toFixed(1)},{(l.y * 100).toFixed(1)} · {((l.visibility ?? 1) * 100).toFixed(0)}%
                  </span>
                </div>
              ))}
          </div>
          <p className="text-[11px] text-slate-400">
            drag a joint to correct it · tweaked frames show orange dots
          </p>
        </>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function seekTo(video: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((resolve) => {
    if (Math.abs(video.currentTime - t) < 1 / 240 && video.readyState >= 2) return resolve();
    const done = () => { video.removeEventListener("seeked", done); resolve(); };
    video.addEventListener("seeked", done);
    video.currentTime = t;
  });
}
